<template>

	<section class="section">
		<div class="container" :class="{ 'is-disabled': productAvailable === false }">
			<h2 class="title is-2 has-text-centered" v-text="data.label"></h2>

			<div class="columns is-reverse my-5">
				<main class="column is-three-fifths">
					<template v-if="hasUsers">
						<div class="level m-0">
							<div class="level-left">
								<div class="level-item">
									<div class="content">
										<h3 class="title is-4 mb-0">Number of <em v-text="data.userType + 's'"></em></h3>
										<p v-if="data.usersMinimum" class="mt-1 is-size-7 has-text-grey">Minimum <span v-text="data.usersMinimum"></span></p>
									</div>
								</div>
							</div>

							<div class="level-right">
								<input
									type="number"
									class="input is-large"
									:class="{ 'is-bumped': isBumped }"
									min="0"
									size="5"
									ref="usercount"
									v-model="selectedUsers"
									@change="enforceMinimum()"
									@focus="stopEnforcingMinimum()"
									@blur="startEnforcingMinimum()"
									@keydown.enter="enforceMinimum()"
								/>
							</div>
						</div>
						<hr />
					</template>

					<h3 class="title is-3" v-if="data.tiersLabel !== false" v-text="data.tiersLabel || 'Select tier'"></h3>

					<article v-for="(tier, key) in data.products" :key="key">
						<input
							type="radio"
							:value="key"
							:id="`${product}-${key}`"
							v-model="selectedTier"
							@change="updateTier()"
							class="is-hidden"
							tabindex="-1"
						/>
						<label :for="`${product}-${key}`" class="columns is-mobile is-vcentered tier-option" :class="{ 'has-quote-button': tier.requiresCustomQuote }" tabindex="1">
							<div class="column">
								<h4 class="title is-4 m-0" v-text="tier.name"></h4>
								<p v-if="tier.description" v-text="tier.description" class="mt-2"></p>
							</div>

							<div class="column is-narrow tier-option-pricing">
								<a v-if="tier.requiresCustomQuote" href="mailto:sales@gearset.com" class="button is-blue is-outlined">Request&nbsp;<span class="is-hidden-touch">custom&nbsp;</span>quote</a>
								<template v-else>
									<p v-text="formatMoney(tier.price)" class="is-size-1 has-text-navy has-text-weight-bold"></p>
									<p v-text="data.per" class="is-size-7"></p>
									<p v-if="tier.priceMinimum" v-text="`Minimum contract size ${formatMoney(tier.priceMinimum)}/mo`" class="is-size-7"></p>
								</template>
							</div>
						</label>
					</article>

					<article>
						<input type="radio" value="" :id="`${product}-none`" v-model="selectedTier" class="is-hidden" />
						<label :for="`${product}-none`" class="columns tier-option is-none">
							<div class="column">
								<h4 class="title is-4">Don’t include</h4>
							</div>
						</label>
					</article>
				</main>
				<aside class="column is-two-fifths">
					<h3 class="title is-4 is-hidden-tablet mb-5">Features included in <span v-text="selectedProduct.name || 'selected tier'"></span></h3>

					<features :product="product" :selected-tier="selectedTier"></features>
				</aside>
			</div>
		</div>
	</section>

</template>

<script>

import Features from './Features.vue';

export default {
	name: 'tier-selector',

	data() {
		return {
			/* Currently selected tier level */
			/* [ starter, teams, enterprise ] or blank for none */
			selectedTier: '',

			/* Currently selected number of users */
			selectedUsers: 0,

			/* Whether the user input should display the bump animation */
			isBumped: false,

			/* Whether the user input should enforce the minimum */
			isEnforced: true,
		};
	},

	computed: {
		data() {
			return this.$store.data[this.product] || {};
		},

		/* Whether this product collects a number of users */
		hasUsers() {
			return 'userType' in this.data;
		},

		/* Object of the currently selected tier */
		selectedProduct() {
			return this.data.products[this.selectedTier] || {};
		},
	},

	methods: {
		/* Send selected tier to the store */
		updateTier() {
			this.$set(this.$store.selection[this.product], 'tier', this.selectedTier);
		},

		/* Send number of users to the store */
		updateUsers() {
			this.$set(this.$store.selection[this.product], 'users', Number(this.selectedUsers));
		},

		/* Stop enforcing the user minimum */
		stopEnforcingMinimum() {
			this.isEnforced = false;
		},

		/* Start enforcing the user minimum, and ensure it is met */
		startEnforcingMinimum() {
			this.isEnforced = true;
			this.enforceMinimum();
		},

		/* Enforce the minimum, and apply any interactivity */
		enforceMinimum(newUsers) {
			const users = Number((newUsers == null) ? this.selectedUsers : newUsers);

			/* Apply minimum user amount, if necessary */
			if (this.minimumUsers && users < this.minimumUsers && this.selectedTier !== '') {
				this.selectedUsers = this.minimumUsers;
			}
			this.updateUsers();

			/* If the number of users is changed to 0, set the tier to none */
			if (users === 0) {
				this.selectedTier = '';

			/* If the number of users is increased from 0, set the tier to the first tier available */
			} else if (users > 0 && this.selectedTier === '') {
				this.selectedTier = Object.keys(this.data.products)[0];
			}
			this.updateTier();
		}
	},

	watch: {
		/* If the user selects a tier, make sure there's at least minimum users */
		/* If the user selects None, make sure the users are reset to 0 */
		selectedTier(newTier) {
			if (Number(this.selectedUsers) === 0 && newTier !== '') {
				this.selectedUsers = this.minimumUsers || 1;
				this.isBumped = true;

				if (this.hasUsers && newTier !== 'enterprise') {
					this.$refs.usercount.focus();
				}
			} else if(newTier === '') {
				this.selectedUsers = 0;
				this.isBumped = false;
			}
			this.updateUsers();
		},

		/* When the selected user count changes */
		selectedUsers(newUsers) {
			if (this.isEnforced) {
				this.enforceMinimum(newUsers);
			}
		},

		/* When product availability changes */
		productAvailable(newAvailability) {
			/* If this product is no longer available, set tier and users to nil */
			if (!newAvailability) {
				this.selectedTier = '';
				this.updateTier();

				this.selectedUsers = 0;
				this.updateUsers();
			}
		},
	},

	props: {
		/* Product name */
		/* [ compare, automation, addons, backup ] */
		product: {
			required: true,
		},

		/* Whether or not this product is available to select */
		/* Boolean or null (assumes true) */
		productAvailable: {},

		/* Minimum contract size of users */
		minimumUsers: {},
	},

	components: {
		features: Features,
	},
};

</script>

<style lang="scss" scoped>
@import '@gearset/web-core/styles/includes/variables';

.container {
	transition: .2s opacity ease-in-out;

	&.is-disabled {
		opacity: .3;
		pointer-events: none;
	}
}

.input {
	width: 8rem;

	&.is-bumped {
		animation: .8s calculator-bump forwards cubic-bezier(.25, 1, .5, 1);
	}
}

.level {
	@include touch {
		text-align: center;
	}
}

.tier-option {
	&:not(.has-quote-button) {
		@include touch {
			flex-direction: column;

			.column {
				width: 100%;

				&:not(:first-child) {
					padding-top: 0;
				}
			}
		}
	}

	&.has-quote-button {
		.column.is-narrow {
			padding-left: 0;
		}

		@include until(480px) {
			.button {
				display: none;
			}
		}
	}
}

.tier-option-pricing {
	@include desktop {
		text-align: right;
	}
}

@keyframes calculator-bump {
	0%,
	100% {
		transform: scale(1);
		background: rgba($yellow, 0);
	}

	15% {
		transform: scale(1.125);
		background: rgba($yellow, .25);
	}
}

</style>
